import { env } from "@/config/env"

export const convertNumberToThousand = (
  value: number | string,
  separator: string = "."
) => {
  if (typeof value === "number") {
    return value
      .toString()
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  }

  return value.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

// Format angka untuk Indonesia (dengan desimal)
export const formatNumberToIndonesian = (value: number | string): string => {
  if (typeof value === "string") {
    value = parseFloat(value) || 0
  }

  // Konversi ke string tanpa memaksa desimal
  const numStr = value.toString()

  // Pisahkan bagian integer dan desimal
  const [integerPart, decimalPart] = numStr.split(".")

  // Tambahkan titik untuk ribuan pada bagian integer
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")

  // Jika ada desimal, tambahkan koma dan desimal
  if (decimalPart) {
    return `${formattedInteger},${decimalPart}`
  }

  // Jika tidak ada desimal, return tanpa koma
  return formattedInteger
}

export const getInitialsName = (name: string) => {
  return name
    .split(" ")
    .map((name) => name[0])
    .join("")
    .toUpperCase()
}

export const unmaskCharacters = (
  value: string,
  returnType: string | number = "string"
) => {
  // remove "Rp", dot, and space
  const numericValue = value.replace(/[^\d]/g, "")

  if (returnType === "number") {
    return parseInt(numericValue, 10)
  }
  return numericValue
}

export const unmaskCharactersString = (value: string): string => {
  // remove "Rp", dot, and space
  return value.replace(/[^\d]/g, "")
}

export const unmaskCharactersNumber = (value: string | number): number => {
  if (typeof value === "string") {
    const numericValue = value.replace(/[^\d]/g, "")
    return parseInt(numericValue, 10)
  }

  return value
}

export const getValidatedDate = (date: string): string => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  return date && dateRegex.test(date) ? date : ""
}

export const getValidatedId = (id: string): number | null => {
  const idRegex = /^\d+$/
  return id && idRegex.test(id) ? parseInt(id) : null
}

export const getFileNameFromUrl = (url: string): string => {
  return url.split("/").pop() || ""
}

export const maskPhoneNumberWithoutCountryCode = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, "")
  const input = digitsOnly.match(/(\d{0,3})(\d{0,})(\d{0,4})/)

  if (input) {
    const firstBlock: string | undefined = input[1]
    const secondBlock: string | undefined = input[2]

    let formattedNumber: string | undefined

    if (!secondBlock) {
      formattedNumber = firstBlock
    } else if (firstBlock && firstBlock.startsWith("0")) {
      formattedNumber = (firstBlock.slice(1) ?? "") + secondBlock
    } else if (firstBlock && firstBlock.startsWith("62")) {
      formattedNumber = (firstBlock.slice(2) ?? "") + secondBlock
    } else {
      formattedNumber = (firstBlock || "") + secondBlock
    }

    return formattedNumber || ""
  }

  // return empty string if input is not a number
  return ""
}

export const phoneRegex = /^\d{10,15}$/

export const getImageCDN = (imagePath?: string) => {
  return imagePath ? `${env.URL_CDN}${imagePath}` : ""
}

export const getAudioCDN = (audioPath?: string) => {
  return audioPath ? `${env.URL_CDN}${audioPath}` : ""
}

export const removeArrayUrlCDN = (values: (string | undefined)[]): string[] => {
  if (!values) return []

  const filterUrlCDN = values.map((item) => {
    if (item && item.includes(env.URL_CDN)) {
      return item.replace(env.URL_CDN, "")
    }
    return item
  })

  return filterUrlCDN.filter((item): item is string => item !== undefined)
}

export const maskNumberOnly = (value: string) => {
  return value.replace(/\D/g, "")
}

export const maskNumberOnlyWithCharacter = (
  value: string,
  allowedChar: string = "."
) => {
  // Buat regex dinamis berdasarkan karakter yang diizinkan
  const regex = new RegExp(`[^0-9${allowedChar}]`, "g")

  // Filter input agar hanya angka dan karakter yang diizinkan
  let sanitized = value.replace(regex, "")

  // Pastikan hanya satu karakter spesial yang diperbolehkan
  const parts = sanitized.split(allowedChar)
  if (parts.length > 2) {
    sanitized = parts[0] + allowedChar + parts.slice(1).join("")
  }

  // Hilangkan leading zero jika bukan angka desimal
  if (!sanitized.startsWith(`${allowedChar}`)) {
    // misal 01 → 1, tapi 0.5 tetap 0.5
    sanitized = sanitized.replace(/^0+(?=\d)/, "")
  }

  return sanitized
}

export const formatCapitalizeCase = (value: string) => {
  return value.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

export const formatUppercase = (value: string) => {
  return value.toUpperCase()
}

export const formatCharacterOnlyAndUppercase = (value: string) => {
  return value.replace(/[^a-zA-Z]/g, "").toUpperCase()
}

export const formatSnakeCase = (value: string, uppercase: boolean = false) => {
  const formatted = value.replace(/\s+/g, "-").toLowerCase()
  return uppercase ? formatted.toUpperCase() : formatted
}

export const formatTitleCase = (value: string) => {
  return value.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
}

export function isObjectTrulyEmpty(obj: Record<string, any>): boolean {
  return Object.values(obj).every((val) => {
    if (typeof val === "string") return val.trim() === ""
    if (typeof val === "number") return val === 0
    if (typeof val === "boolean") return val === false
    if (Array.isArray(val)) return val.length === 0
    if (typeof val === "object" && val !== null)
      return Object.keys(val).length === 0
    return val === null || val === undefined
  })
}

export const truncateString = (str: string, maxLength: number = 19) => {
  if (str.length <= maxLength) return str

  return str.slice(0, maxLength) + "..."
}

/**
 * Format schedule shift label
 * @param label - The label to format
 * @returns The formatted label
 * @example
 * const formattedLabel = formatScheduleShiftLabel("P-PAGI-08")
 * console.log(formattedLabel)
 * // Output: P (PAGI-08) | P (A1) | M | OFF
 */
export const formatScheduleShiftLabel = (label: string) => {
  const parts = label.split("-")
  if (parts.length === 1) return label

  const shift = parts[0]
  const time = parts.slice(1).join("-")
  return time ? `${shift} (${time})` : shift
}

/**
 * Format schedule shift label get time
 * @param label - The label to format
 * @returns The formatted label
 * @example
 * const { schedule, time } = formatScheduleShiftLabelGetTime("P-PAGI-08")
 * console.log(schedule)
 * // Output: P
 * console.log(time)
 * // Output: PAGI-08
 */
export const formatScheduleShiftLabelGetTime = (label: string) => {
  const parts = label.split("-")
  if (parts.length === 1) {
    return {
      schedule: parts[0],
      time: "",
    }
  }
  return {
    schedule: parts[0],
    time: parts.slice(1).join("-"),
  }
}

export const onlyLettersAndNumbers = (
  value: string,
  uppercase: boolean = false
) => {
  const formatted = value.replace(/[^a-zA-Z0-9]/g, "")
  return uppercase ? formatted.toUpperCase() : formatted
}

// format price
export const formatPrice = (value: number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value)
}

/**
 * Mengubah string menjadi huruf kapital dan hanya menyisakan huruf A-Z dan underscore (_)
 * @param value - string input dari user
 * @returns string yang sudah diformat
 */
export function formatToCapitalUnderscore(value: string): string {
  return value
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z_]/g, "")
}

export const parseNumber = (value: string): number => {
  return Number(value.replace(/[^0-9]/g, ""))
}

/**
 * Format input angka desimal agar hanya boleh maksimal 2 digit di belakang koma.
 * Contoh: "2.43", "99.99", "100", "0.5"
 * Hanya mengizinkan angka dan satu titik desimal.
 */
export function formatDecimalInput(value: string, maxDecimal: number = 2) {
  // Hapus karakter selain angka dan titik
  let sanitized = value.replace(/[^0-9.]/g, "")
  // Hanya boleh satu titik
  const parts = sanitized.split(".")
  if (parts.length > 2) {
    sanitized = parts[0] + "." + parts.slice(1).join("")
  }
  // Batasi digit di belakang koma
  if (sanitized.includes(".")) {
    const [intPart, decPart] = sanitized.split(".")
    sanitized = intPart + "." + (decPart.slice(0, maxDecimal) || "")
  }
  // Hilangkan leading zero kecuali sebelum titik
  sanitized = sanitized.replace(/^0+(?=\d)/, "")
  return sanitized
}

/**
 * Untuk input tipe Fixed: hanya izinkan angka, lalu format ribuan.
 * Tidak membatasi jumlah digit.
 */
export function handleFixedInputChange(value: string): string {
  const raw = value.replace(/[^0-9]/g, "")
  return convertNumberToThousand(raw)
}

/**
 * Untuk input tipe Percentage: hanya izinkan angka dan satu titik, maksimal 2 digit di belakang koma,
 * dan jika sudah 100 tidak boleh titik/desimal.
 */
export function handlePercentageInputChange(value: string): string {
  let sanitized = value.replace(/[^0-9.]/g, "")
  // Hanya boleh satu titik
  const parts = sanitized.split(".")
  if (parts.length > 2) {
    sanitized = parts[0] + "." + parts.slice(1).join("")
  }
  // Batasi digit di belakang koma
  if (sanitized.includes(".")) {
    const [intPart, decPart] = sanitized.split(".")
    sanitized = intPart + "." + (decPart.slice(0, 2) || "")
  }
  // Jika sudah 100, tidak boleh titik/desimal
  if (sanitized === "100" || sanitized.startsWith("100.")) {
    sanitized = "100"
  } else {
    // Cek bagian sebelum titik, jika > 100, set ke 100
    const [intPart] = sanitized.split(".")
    if (parseInt(intPart) > 100) {
      sanitized = "100"
    }
  }
  // Hilangkan leading zero kecuali sebelum titik
  sanitized = sanitized.replace(/^0+(?=\d)/, "")
  return sanitized
}
